import React, { useLayoutEffect, useMemo } from "react";
import { Platform, SectionList } from "react-native";
import { graphql, useLazyLoadQuery } from "react-relay";
import { LazyQueryNetworkBoundrary } from "../../components/mobile/QueryNetworkBoundrary";
import { UserLoggedInNavigatorScreenProps } from "../../TopLevelNavigators/UserLoggedIn/UserLoggedInNavigatorParamList";
import { withWebModalStack } from "@/src/TopLevelNavigators/UserLoggedIn/withWebModalStack";
import {
  AHSeparator,
  AHText,
  Box,
  ListItem,
  useBoxStyle,
  useTheme,
} from "arkh";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import { useSizeClass } from "../../common/Utils/useSizeClass";
import { SizeClass } from "@/src/common/Utils/SizeClass";
import { useIntlTokens } from "../../components/mobile/useIntlTokens";
import { FormattedMessage } from "react-intl";
import { SignalOpenedByScreenQuery } from "./__generated__/SignalOpenedByScreenQuery.graphql";
import { StoreBanner } from "../../components/OrgHeaders/StoreBanner";
import { HeaderButton } from "../../components";
import { Header } from "@react-navigation/elements";
import { FormattedDate } from "../../common/dates/FormattedDate";
import { TimeZoneProvider } from "../../OS/Timezone";

export const SignalOpenedByScreen = withWebModalStack(
  (props: UserLoggedInNavigatorScreenProps<"SignalOpenedBy">) => {
    const backgroundColor = useTheme()["surface-background-light"];
    useLayoutEffect(() => {
      props.navigation.setOptions({
        headerShown: false,
        contentStyle: {
          backgroundColor,
        },
      });
    }, [props.navigation, backgroundColor]);
    return (
      <TimeZoneProvider timezone={props.route.params.storeTimezone}>
        {props.route.params.storeName ? (
          <StoreBanner storeName={props.route.params.storeName} />
        ) : null}
        <Box spaceHorizontal={Platform.OS === "web" ? "none" : "medium"}>
          <Header
            headerStatusBarHeight={
              Platform.OS === "android" && props.route.params.storeName
                ? undefined
                : 4
            }
            headerLeft={() => (
              <HeaderButton type="CLOSE" onPress={props.navigation.goBack} />
            )}
            title=""
          />
        </Box>
        <LazyQueryNetworkBoundrary newFetchKeyOnNavigation>
          {(fetchKey) => (
            <InnerSignalOpenedByScreen
              fetchKey={fetchKey}
              signalId={props.route.params.signalId}
            />
          )}
        </LazyQueryNetworkBoundrary>
      </TimeZoneProvider>
    );
  }
);

function InnerSignalOpenedByScreen(props: {
  fetchKey: string;
  signalId: string;
}) {
  const data = useLazyLoadQuery<SignalOpenedByScreenQuery>(
    graphql`
      query SignalOpenedByScreenQuery($signalId: ID!) {
        signal(id: $signalId) {
          id
          receiverReceipts {
            openedAtDate {
              ...FormattedDate
            }
            user {
              id
              name
              email
            }
          }
          store {
            users {
              id
              user {
                id
                name
                email
              }
            }
          }
        }
      }
    `,
    {
      signalId: props.signalId,
    },
    {
      fetchKey: props.fetchKey,
      fetchPolicy: "store-and-network",
    }
  );
  const intl_tokens = useIntlTokens();

  const opened = useMemo(
    () =>
      data.signal.receiverReceipts.flatMap((receipt) =>
        receipt.user
          ? ([
              {
                ...receipt.user,
                openedAtDate: receipt.openedAtDate,
              },
            ] as const)
          : []
      ),
    [data.signal.receiverReceipts]
  );

  const unread = useMemo(
    () =>
      data.signal.store.users
        .flatMap((receipt) =>
          receipt.user
            ? ([
                {
                  ...receipt.user,
                  openedAtDate: undefined,
                },
              ] as const)
            : []
        )
        .filter((u) => opened.findIndex((_u) => u.id === _u.id) === -1),
    [data.signal.store.users, opened]
  );

  type Item = (typeof opened)[number] | (typeof unread)[number];
  const sections = useMemo(() => {
    return [
      ...(unread.length > 0
        ? [
            {
              key: "unread" as const,
              title: intl_tokens.Unread,
              data: unread as Item[],
            },
          ]
        : []),
      ...(opened.length > 0
        ? [
            {
              key: "opened" as const,
              title: intl_tokens.Opened,
              data: opened as Item[],
            },
          ]
        : []),
    ];
  }, [intl_tokens.Unread, intl_tokens.Opened, unread, opened]);

  const safeAreaInsets = useSafeAreaInsets();
  const isCompact = useSizeClass().width === SizeClass.COMPACT;
  const insets = isCompact
    ? {
        bottom: safeAreaInsets.bottom,
      }
    : {};
  const contentBox = useBoxStyle({
    spaceHorizontal: "medium",
    spaceBottom: "medium",
    insets,
  });

  return (
    <SectionList
      contentContainerStyle={contentBox}
      sections={sections}
      stickySectionHeadersEnabled={false}
      renderItem={({ item, index, section }) => {
        return (
          <ListItem
            disabled
            isFirst={index === 0}
            isLast={index === section.data.length - 1}
            right={
              item.openedAtDate ? (
                <AHText type="detail">
                  <FormattedMessage
                    defaultMessage="Opened at {openedAtFormatted}"
                    description="SignalOpenedByScreen openedAt list item text"
                    values={{
                      openedAtFormatted: (
                        <FormattedDate date={item.openedAtDate} />
                      ),
                    }}
                  />
                </AHText>
              ) : null
            }
          >
            <ListItem.Title>{item.name}</ListItem.Title>
            <ListItem.SubTitle>{item.email}</ListItem.SubTitle>
          </ListItem>
        );
      }}
      ItemSeparatorComponent={AHSeparator}
      renderSectionHeader={({ section }) => {
        return (
          <AHText
            spaceHorizontal="medium"
            spaceTop="medium"
            spaceBottom="small"
          >
            {section.title}
          </AHText>
        );
      }}
    />
  );
}
